import { z } from 'zod';
import { dateTimeSchema, uidSchema } from './shared';

export const quickReplySchema = z.object({
  text: z.string().min(1),
});

export interface QuickReply extends z.infer<typeof quickReplySchema> {}

export const chatMessagesSchema = z.object({
  content: z.string().min(1).max(20_000),
  content_html: z.string().optional(),
  auto_recipient: z.string().optional(),
  created_at: dateTimeSchema.default(Date.now()),
  is_img: z.boolean().default(false),
  is_auto: z.boolean().default(false),
  mime_type: z.string().optional(),
  offer_amount: z.number().optional(), // same as offer price or counter offer amount
  payment_request_id: z.string().optional(), // links to a payment_requests/ doc
  skip_notification: z.boolean().optional(),
  thumbnail: z.string().optional(),
  download_url: z.string().optional(),
  uid: uidSchema,
  quick_replies: z.array(quickReplySchema).optional(),
  quick_replies_title: z.string().optional(),
});

export const chatSchema = z.object({
  buyer_id: uidSchema,
  case_num: z.number().gt(1).optional(),
  created_at: dateTimeSchema.default(Date.now()),
  flagged: z.boolean().default(false),
  from_web: z.boolean().default(true),
  id: z.string().min(10),
  is_offer: z.boolean().default(false),
  last_time: z.number().optional().default(0),
  messages: z.array(chatMessagesSchema).default([]),
  offer_id: z.string().optional(),
  order_id: z.string().optional(),
  order_num: z.number().optional(),
  product_id: z.string().optional(),
  seller_id: uidSchema,
  uids: z.array(uidSchema).max(3).default([]), // [buyer_id, seller_id]
  unread: z.record(z.boolean()).default({}),
  is_expert: z.boolean().default(false),
  expert_state: z.number().int().gte(0).lte(4).optional(),
  is_auto: z.boolean().default(false).optional(),
  support_state: z.number().int().optional(), // 0: New Ticket, 1: Awaiting Customer Reply, 2: Awaiting Support Reply, 3: Solved, 4: Cancelled, 5: Awaiting Seller, 6: Internal Review
  support_assigned: uidSchema.optional(),
  favorited: z.array(z.string()).optional(),
  support_source: z.string().optional(), // phone_support, claims, ai_support
});

export enum SupportState {
  NewTicket = 0,
  AwaitingCustomerReply = 1,
  AwaitingSupportReply = 2,
  Resolved = 3,
  Cancelled = 4,
  AwaitingSeller = 5,
  InternalReview = 6,
}

export const SupportStateToLabel = {
  [SupportState.NewTicket]: 'New Ticket',
  [SupportState.AwaitingCustomerReply]: 'Awaiting Customer Reply',
  [SupportState.AwaitingSupportReply]: 'Awaiting Support Reply',
  [SupportState.Resolved]: 'Resolved',
  [SupportState.Cancelled]: 'Cancelled',
  [SupportState.AwaitingSeller]: 'Awaiting Seller',
  [SupportState.InternalReview]: 'Internal Review',
};

export enum SupportSource {
  PhoneSupport = 'phone_support',
  Claims = 'claims',
  AiSupport = 'ai_support',
}

export const SupportSourceToLabel = {
  [SupportSource.PhoneSupport]: 'Phone Support',
  [SupportSource.Claims]: 'Claims',
  [SupportSource.AiSupport]: 'AI Support',
};

export const SupportStateList = [
  SupportStateToLabel[SupportState.NewTicket],
  SupportStateToLabel[SupportState.AwaitingCustomerReply],
  SupportStateToLabel[SupportState.AwaitingSupportReply],
  SupportStateToLabel[SupportState.Resolved],
  SupportStateToLabel[SupportState.Cancelled],
  SupportStateToLabel[SupportState.AwaitingSeller],
  SupportStateToLabel[SupportState.InternalReview],
];

export interface ChatMessageInput extends z.input<typeof chatMessagesSchema> {
  created_at: number;
}
export interface ChatMessageDocument
  extends z.output<typeof chatMessagesSchema> {}
export interface ChatInput extends z.input<typeof chatSchema> {
  created_at: number;
}
export interface ChatDocument extends z.output<typeof chatSchema> {
  // for caching
  has_unread?: boolean;
  last_msg?: string;
}
